import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { fetchPublicMenu, type PublicMenu } from '../services/publicMenu'
import { MenuView } from '../components/menu/MenuView'
import { fetchQrTable } from '../services/tableQr'
import type { TableMode } from '../components/menu/TableOrder'
import { ClockIcon } from '../components/icons'
import './MenuPage.css'

/**
 * The menu a customer sees after scanning the code on their table.
 *
 * No session, no ordering, no prices to pay here, it answers one question,
 * "what is there and what does it cost", and the one thing it does that a
 * printed menu cannot: it knows what has run out.
 *
 * Deliberately outside every provider the rest of the app runs in. Those
 * providers all assume a signed-in member of an institution; this page has no
 * one signed in at all, and mounting it inside them would have it opening
 * subscriptions on behalf of a stranger.
 */
export function MenuPage() {
  const params = useParams<{ tenantId?: string; masa?: string }>()
  const [search] = useSearchParams()
  // Both spellings work: /m/emirler is what a printed code carries, ?yurt=
  // is what the rest of the app already uses and what someone will paste.
  const tenantId = (params.tenantId ?? search.get('yurt') ?? '').trim().toLowerCase()

  /** Masanın karekodu: /m/<kurum>/<masa anahtarı> (QR Sipariş). */
  const token = (params.masa ?? search.get('masa') ?? '').trim()
  const [table, setTable] = useState<TableMode | undefined>()
  const [menu, setMenu] = useState<PublicMenu | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'expired' | 'error'>('loading')

  useEffect(() => {
    if (!tenantId) {
      setState('missing')
      return
    }
    let cancelled = false
    setState('loading')

    // Masa bulunamazsa ya da modül kapalıysa menü yine açılıyor, siparişsiz.
    if (token) {
      fetchQrTable(tenantId, token)
        .then((info) => !cancelled && info && setTable({ tenantId, token, info }))
        .catch((err) => console.error('[menu] masa', err))
    }

    fetchPublicMenu(tenantId)
      .then((data) => {
        if (cancelled) return
        // Kurum yok ya da QR menüsü kapalı: ikisi de aynı cevap (fonksiyon
        // hangisi olduğunu bilerek söylemiyor). Müşteri karekodu okutmuş,
        // "bulunamadı" yerine bağlantının süresi doldu diyoruz (1 Kas 2026,
        // kullanıcı).
        if (!data) {
          setState('expired')
          return
        }
        setMenu(data)
        setState('ready')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[menu]', err)
        setState('error')
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, token])

  if (state === 'loading') {
    return (
      <div className="pubmenu pubmenu--center">
        <p className="pubmenu__note">Menü yükleniyor…</p>
      </div>
    )
  }

  if (state === 'missing') {
    return (
      <div className="pubmenu pubmenu--center">
        <h1 className="pubmenu__oops">Menü bulunamadı</h1>
        <p className="pubmenu__note">
          Karekod eksik ya da bu işletme menüsünü paylaşmıyor olabilir.
        </p>
      </div>
    )
  }

  if (state === 'expired') {
    return (
      <div className="pubmenu pubmenu--center">
        <div className="pubmenu__gone">
          <span className="pubmenu__gone-icon" aria-hidden="true">
            <ClockIcon />
          </span>
          <h1 className="pubmenu__oops">Bu bağlantının süresi doldu</h1>
          <p className="pubmenu__note">
            Bu karekod artık geçerli değil. Menü için işletme çalışanlarına danışabilirsiniz.
          </p>
        </div>
      </div>
    )
  }

  if (state === 'error' || !menu) {
    return (
      <div className="pubmenu pubmenu--center">
        <h1 className="pubmenu__oops">Menü açılamadı</h1>
        <p className="pubmenu__note">
          Bağlantınızı kontrol edip sayfayı yenileyin.
        </p>
      </div>
    )
  }

  return <MenuView menu={menu} style={menu.style} favoritesKey={tenantId} table={table} />
}
